/**
 * Authentication against Stalwart's OAuth 2.0 endpoints.
 *
 * Stalwart does NOT support the `password` grant — its metadata advertises only
 * authorization_code, implicit and device_code, and a password grant returns
 * `invalid_grant`. The stock authorization_code flow is also unusable here: its
 * /authorize/code endpoint serves Stalwart's WASM admin bundle, so redirecting
 * there would replace our sign-in screen with an admin console.
 *
 * The flow below is the one that works and keeps the product's own login page:
 *
 *   1. POST /api/oauth  with HTTP Basic (the user's credentials) and
 *      {"type":"code", client_id, redirect_uri}      -> { data: { code } }
 *   2. POST /auth/token with grant_type=authorization_code&code=…
 *                                                     -> { access_token, refresh_token }
 *   3. Keep the token. Discard the password.
 *
 * The password is used for exactly one request and is never written to storage.
 * Tokens expire in ~1h, so `refresh` renews without re-prompting.
 *
 * Verified end to end against Stalwart v0.15.5: token authenticates to JMAP.
 */

const TOKEN_KEY = "syndeo.mail.token";
const REFRESH_KEY = "syndeo.mail.refresh";
const USER_KEY = "syndeo.mail.user";

const CLIENT_ID = "syndeo-mail";
/** Not a real navigation target — Stalwart only echoes it back. */
const REDIRECT_URI = `${location.origin}/`;

export interface Session {
  token: string;
  refreshToken: string | null;
  email: string;
}

export class AuthError extends Error {}

interface TokenResponse {
  access_token?: string;
  refresh_token?: string;
  expires_in?: number;
  error_description?: string;
}

/** Step 1 — trade credentials for a short-lived authorization code. */
async function authorizationCode(email: string, password: string): Promise<string> {
  const res = await fetch("/api/oauth", {
    method: "POST",
    // The credential is the Basic header; Stalwart's own session cookie should
    // not ride along with it. See JmapClient.http().
    credentials: "omit",
    headers: {
      authorization: `Basic ${btoa(`${email}:${password}`)}`,
      "content-type": "application/json",
    },
    body: JSON.stringify({ type: "code", client_id: CLIENT_ID, redirect_uri: REDIRECT_URI }),
  });

  if (res.status === 401 || res.status === 403) {
    // Deliberately generic — never reveal whether the address exists.
    throw new AuthError("That email address and password don't match.");
  }
  if (!res.ok) throw new AuthError("Could not sign in. Please try again.");

  const body = (await res.json()) as { data?: { code?: string } };
  const code = body.data?.code;
  if (!code) throw new AuthError("Could not sign in. Please try again.");
  return code;
}

/** Step 2 — exchange the code for a bearer token. */
async function exchange(params: Record<string, string>): Promise<TokenResponse> {
  const res = await fetch("/auth/token", {
    method: "POST",
    credentials: "omit",
    headers: { "content-type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({ client_id: CLIENT_ID, ...params }),
  });
  const body = (await res.json().catch(() => ({}))) as TokenResponse;
  if (!res.ok || !body.access_token) {
    throw new AuthError(body.error_description ?? "Could not sign in. Please try again.");
  }
  return body;
}

export async function signIn(email: string, password: string): Promise<Session> {
  const code = await authorizationCode(email, password);
  const token = await exchange({
    grant_type: "authorization_code",
    code,
    redirect_uri: REDIRECT_URI,
  });
  const session: Session = {
    token: token.access_token!,
    refreshToken: token.refresh_token ?? null,
    email,
  };
  persist(session);
  return session;
}

/** Renews an expired access token without re-prompting. */
export async function refresh(session: Session): Promise<Session> {
  if (!session.refreshToken) throw new AuthError("Sign-in expired");
  const token = await exchange({
    grant_type: "refresh_token",
    refresh_token: session.refreshToken,
  });
  const next: Session = {
    token: token.access_token!,
    refreshToken: token.refresh_token ?? session.refreshToken,
    email: session.email,
  };
  persist(next);
  return next;
}

function persist(session: Session): void {
  try {
    // sessionStorage, not localStorage: the token dies with the tab, which is
    // the right default for a mailbox on a shared machine.
    sessionStorage.setItem(TOKEN_KEY, session.token);
    sessionStorage.setItem(USER_KEY, session.email);
    if (session.refreshToken) sessionStorage.setItem(REFRESH_KEY, session.refreshToken);
  } catch {
    /* private browsing — the session just won't survive a reload */
  }
}

export function restore(): Session | null {
  try {
    const token = sessionStorage.getItem(TOKEN_KEY);
    const email = sessionStorage.getItem(USER_KEY);
    if (!token || !email) return null;
    return { token, email, refreshToken: sessionStorage.getItem(REFRESH_KEY) };
  } catch {
    return null;
  }
}

export function signOut(): void {
  try {
    [TOKEN_KEY, REFRESH_KEY, USER_KEY].forEach((k) => sessionStorage.removeItem(k));
  } catch {
    /* nothing to clear */
  }
}
